import { useEffect, useReducer, useRef } from 'react';

/**
 * react-select positions a body-portaled menu from the control's bounding rect
 * at render time and does NOT recompute it when the control itself RESIZES
 * (only on scroll / menu-open / window resize). For multi-value selects this
 * shows up as a bug: adding or removing a chip changes the control's height, so
 * the open menu detaches and floats away from the (now shorter) control.
 *
 * Attach the returned ref to a wrapper element around the select. A
 * ResizeObserver fires after the control's height settles and forces a
 * re-render, which makes react-select recompute the portaled menu's position
 * against the new rect — keeping the menu anchored to the control.
 */
export const useMenuRepositionOnResize = () => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [, reposition] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    const node = wrapperRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => reposition());
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return wrapperRef;
};
